import React, { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react'
import { PercentageOutlined } from '@ant-design/icons'
import { FiMousePointer } from 'react-icons/fi'
import { Button } from 'antd'
import { KPICardSpark } from '@pushly/aqe/lib/components/kpi-card/kpi-card-spark'
import { KPICardMini } from '@pushly/aqe/lib/components/kpi-card/kpi-card-mini'
import { useAppState } from '../../hooks/use-mobile-service'
import { useKPIMetrics } from '../../../hooks/use-kpi-metrics'
import './kpi-summary.scss'

export interface KPISummaryProps {
    /** Called when KPI data is fetched — provides lastUpdated timestamp and refresh function */
    onUpdate?: (lastUpdated: Date, refresh: () => void) => void
}

export const KPISummary: React.FC<KPISummaryProps> = observer(({ onUpdate }) => {
    const appState = useAppState()
    const history = useHistory()
    const { data, loading, error, lastUpdated, refresh } = useKPIMetrics()

    // Notify parent of updates
    useEffect(() => {
        if (lastUpdated && onUpdate) {
            onUpdate(lastUpdated, refresh)
        }
    }, [lastUpdated, onUpdate, refresh])

    const handleTap = useCallback(() => {
        const domainId = appState.currentDomain?.id
        if (domainId) {
            history.push(`/domains/${domainId}/insights`)
        }
    }, [appState, history])

    if (error && !data) {
        return (
            <div className="kpi-summary">
                <div className="kpi-summary__error" role="alert">
                    <span className="kpi-summary__error-message">Failed to load metrics</span>
                    <Button size="small" onClick={refresh} aria-label="Retry loading metrics">
                        Retry
                    </Button>
                </div>
            </div>
        )
    }

    // Loading state — show skeleton cards
    if (loading && !data) {
        return (
            <div className="kpi-summary">
                <KPICardSpark
                    label="Push Subscribers"
                    formattedValue=""
                    sparkData={[]}
                    loading
                    showRange={false}
                    showMore={false}
                />
                <div className="kpi-summary__mini-row">
                    <KPICardMini label="Clicks" formattedValue="" loading />
                    <KPICardMini label="CTR" formattedValue="" loading />
                </div>
            </div>
        )
    }

    if (!data || data.length === 0) {
        return null
    }

    // First metric = Push Subscribers (spark card), rest = mini tiles
    const [subscribers, ...secondary] = data

    return (
        <div className="kpi-summary">
            <KPICardSpark
                label={subscribers.label}
                formattedValue={subscribers.formattedValue}
                secondaryValue={subscribers.secondaryValue}
                sparkData={subscribers.sparkData ?? []}
                chartType="bar"
                footnote="Last 7 days"
                onTap={handleTap}
                showRange={false}
                showMore={false}
            />
            {secondary.length > 0 && (
                <div className="kpi-summary__mini-row">
                    {secondary.map((metric) => (
                        <KPICardMini
                            key={metric.label}
                            label={metric.label}
                            formattedValue={metric.formattedValue}
                            trend={metric.trend}
                            trendPercent={metric.trendPercent}
                            comparisonLabel="vs prev day"
                            subtitle="Yesterday"
                            icon={metric.label === 'Clicks' ? <FiMousePointer /> : <PercentageOutlined />}
                            tileColor={metric.label === 'Clicks' ? 'purple' : 'teal'}
                            onTap={handleTap}
                        />
                    ))}
                </div>
            )}
        </div>
    )
})

KPISummary.displayName = 'KPISummary'
